import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  ChevronLeft,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Camera,
  Save,
  Edit2,
  Check,
  X,
  Shield,
  Users,
  ChevronRight,
} from "lucide-react";

const Profile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [profileData, setProfileData] = useState({
    displayName: user?.displayName || "",
    email: user?.email || "",
    phone: user?.phoneNumber || "",
    location: "",
    bio: "",
  });

  const handleInputChange = (field, value) => {
    setProfileData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    // Simulate save operation
    setTimeout(() => {
      setIsSaving(false);
      setIsEditing(false);
      // Here you would dispatch an action to update user profile in Firebase
    }, 1500);
  };

  const handleCancel = () => {
    setProfileData({
      displayName: user?.displayName || "",
      email: user?.email || "",
      phone: user?.phoneNumber || "",
      location: "",
      bio: "",
    });
    setIsEditing(false);
  };

  const profileFields = [
    {
      id: "displayName",
      label: "Full Name",
      icon: User,
      placeholder: "Enter your full name",
      type: "text",
    },
    {
      id: "email",
      label: "Email Address",
      icon: Mail,
      placeholder: "your.email@example.com",
      type: "email",
      disabled: true, // Email usually can't be changed
    },
    {
      id: "phone",
      label: "Phone Number",
      icon: Phone,
      placeholder: "+1 (555) 000-0000",
      type: "tel",
    },
    {
      id: "location",
      label: "Location",
      icon: MapPin,
      placeholder: "City, Country",
      type: "text",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-[#172554] pt-10 pb-24 rounded-b-[40px] shadow-2xl">
        <div className="px-6">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate("/settings")}
              className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-all backdrop-blur-md border border-white/20"
            >
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            <h1 className="text-2xl font-black italic text-white">Profile</h1>

            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-all backdrop-blur-md border border-white/20"
              >
                <Edit2 className="w-5 h-5 text-white" />
              </button>
            ) : (
              <div className="w-10" />
            )}
          </div>

          <p className="text-sm text-white/70 font-medium">
            Manage your personal information
          </p>
        </div>
      </div>

      {/* Profile Picture Section */}
      <div className="px-6 -mt-16 mb-6">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6">
          <div className="flex flex-col items-center">
            <div className="relative">
              <div className="w-24 h-24 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center shadow-xl">
                <span className="text-4xl font-black text-white">
                  {(profileData.displayName || user?.email || "U")
                    .charAt(0)
                    .toUpperCase()}
                </span>
              </div>
              {isEditing && (
                <button className="absolute bottom-0 right-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center shadow-lg hover:bg-blue-700 transition-all border-2 border-white">
                  <Camera className="w-4 h-4 text-white" />
                </button>
              )}
            </div>
            <h2 className="text-xl font-black text-slate-900 mt-4">
              {profileData.displayName || "User"}
            </h2>
            <p className="text-sm text-slate-500 font-medium">
              {profileData.email}
            </p>
            <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
              <Calendar className="w-3 h-3" />
              <span>Member since {new Date().getFullYear()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Information */}
      <div className="px-6 space-y-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="p-4 border-b border-slate-100 bg-slate-50">
            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              Personal Information
            </h3>
          </div>

          <div className="divide-y divide-slate-100">
            {profileFields.map((field) => {
              const Icon = field.icon;
              return (
                <div key={field.id} className="p-4">
                  <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">
                    {field.label}
                  </label>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-slate-400" />
                    </div>
                    {isEditing && !field.disabled ? (
                      <input
                        type={field.type}
                        value={profileData[field.id]}
                        onChange={(e) =>
                          handleInputChange(field.id, e.target.value)
                        }
                        placeholder={field.placeholder}
                        className="flex-1 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    ) : (
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-slate-900">
                          {profileData[field.id] || field.placeholder}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bio Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="p-4 border-b border-slate-100 bg-slate-50">
            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              About
            </h3>
          </div>
          <div className="p-4">
            {isEditing ? (
              <textarea
                value={profileData.bio}
                onChange={(e) => handleInputChange("bio", e.target.value)}
                placeholder="Tell us about yourself..."
                rows={4}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            ) : (
              <p className="text-sm text-slate-700 leading-relaxed">
                {profileData.bio || "No bio added yet."}
              </p>
            )}
          </div>
        </div>

        {/* Admin Section - Only show for admin users */}
        {/* You can add a condition here: {user?.role === 'admin' && ( ... )} */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="p-4 border-b border-slate-100 bg-gradient-to-r from-purple-50 to-blue-50">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-purple-600" />
              <h3 className="text-[10px] font-black text-purple-600 uppercase tracking-widest">
                Admin Panel
              </h3>
            </div>
          </div>

          <div className="divide-y divide-slate-100">
            <button
              onClick={() => navigate("/users")}
              className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-black text-slate-900">
                    Manage Users
                  </p>
                  <p className="text-xs text-slate-500 font-medium">
                    View and manage all users
                  </p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        {isEditing && (
          <div className="flex gap-3 pb-6">
            <button
              onClick={handleCancel}
              className="flex-1 py-4 bg-slate-100 text-slate-600 rounded-xl font-black text-sm uppercase tracking-widest hover:bg-slate-200 transition-all flex items-center justify-center gap-2"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 py-4 bg-blue-600 text-white rounded-xl font-black text-sm uppercase tracking-widest hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg"
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        )}

        {/* Info Card */}
        {!isEditing && (
          <div className="bg-blue-50 rounded-2xl p-4 border border-blue-100 mb-6">
            <div className="flex items-start gap-3">
              <User className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="text-sm font-black text-blue-900 mb-1">
                  Keep Your Profile Updated
                </h4>
                <p className="text-xs text-blue-700">
                  Maintaining accurate profile information helps us provide you
                  with better service and personalized experience.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
